/**
 * ReviewEditPreviewStep
 *
 * Step 3 of the review edit wizard: preview of all review data
 * with save/back actions.
 * Used inside CustomerReviewEdit's progress Card.
 */
import { Star, ThumbsUp, ThumbsDown } from 'lucide-react';
import Button from '../common/Button';

export default function ReviewEditPreviewStep({ reviewData, onSave, canSave, saving, onBack }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Your Changes</h3>
        <p className="text-sm text-gray-600 mb-6">
          Preview your updated review before saving
        </p>
      </div>

      {/* Ratings Preview */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Overall Rating</h4>
        <div className="flex items-center space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-6 h-6 ${
                star <= reviewData.overall_rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              }`}
            />
          ))}
          <span className="ml-2 text-lg font-medium text-gray-700">
            {reviewData.overall_rating.toFixed(1)}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          {[
            { label: 'Communication', value: reviewData.communication_rating },
            { label: 'Knowledge', value: reviewData.knowledge_rating },
            { label: 'Professionalism', value: reviewData.professionalism_rating },
            { label: 'Value', value: reviewData.value_rating }
          ].map((category) => (
            <div key={category.label}>
              <p className="text-xs text-gray-600">{category.label}</p>
              <div className="flex items-center space-x-1 mt-1">
                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                <span className="text-sm font-medium text-gray-900">{category.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Written Review Preview */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold text-gray-900 mb-2">Your Review</h4>
        <p className="text-gray-700 leading-relaxed">{reviewData.review_text}</p>

        <div className="mt-4 flex items-center space-x-2">
          {reviewData.would_recommend ? (
            <>
              <ThumbsUp className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">Recommends this experience</span>
            </>
          ) : (
            <>
              <ThumbsDown className="w-5 h-5 text-red-600" />
              <span className="text-sm font-medium text-red-700">Does not recommend this experience</span>
            </>
          )}
        </div>

        {reviewData.trip_highlights && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Highlights</p>
            <p className="text-sm text-gray-700">{reviewData.trip_highlights}</p>
          </div>
        )}

        {reviewData.areas_improvement && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Areas for Improvement</p>
            <p className="text-sm text-gray-700">{reviewData.areas_improvement}</p>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <Button onClick={onSave} disabled={!canSave || saving}>
          {saving ? 'Saving Changes...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
